import { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Container, Breadcrumb, Button } from 'react-bootstrap'
import useOrganization from 'hooks/use-organization'
import SubscriptionConfiguration from 'components/stripe-update-page/subscription-configuration'
import InvoicePreview from 'components/stripe-update-page/invoice-preview'
import { toast } from 'react-hot-toast'
import {
  LINQ_BLUE_INFRASTRUCTURE_PRODUCT_ID,
  LINQ_BLUE_PRODUCT_IDS,
  STRIPE_PRIMARY,
  STRIPE_PRIMARY_HOVER,
} from 'constants/stripe'
import { BASE_API_URL } from 'constants/env'
import { store } from 'index'
import { apiHeaders } from 'utils/api-headers'

const StripeUpdatePage = () => {
  const params = useParams<{ id: string }>()
  const { id: orgId } = params
  const history = useHistory()

  const { data: org, isFetching } = useOrganization(orgId)

  const [subscriptionItems, setSubscriptionItems] = useState<any[]>([])
  const [originalSubscriptionItems, setOriginalSubscriptionItems] = useState<
    any[]
  >([])
  const [resetBillingInterval, setResetBillingInterval] =
    useState<boolean>(false)
  const [prorationSettings, setProrationSettings] = useState({
    enabled: false,
    behavior: 'none' as 'next_invoice' | 'immediately' | 'none',
  })
  const [trialDays, setTrialDays] = useState<number | null>(null)
  const [metadata, setMetadata] = useState<
    Array<{ key: string; value: string }>
  >([])
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null)
  const [paymentDueDays, setPaymentDueDays] = useState<number>(15)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [coupons, setCoupons] = useState<any[]>([])
  const [previewError, setPreviewError] = useState<boolean>(false)
  const [linqQuantityError, setLinqQuantityError] = useState<boolean>(false)
  const [invoicePreviewData, setInvoicePreviewData] = useState<any>(null)

  // Initialize trial days if organization has an existing trial
  useEffect(() => {
    if (
      org?.organization_subscription?.subscription_status === 'trialing' &&
      org?.organization_subscription?.trial_end
    ) {
      const trialEnd = new Date(org.organization_subscription.trial_end)
      const now = new Date()

      // Reset both dates to midnight for accurate day calculation
      const trialEndDate = new Date(
        trialEnd.getFullYear(),
        trialEnd.getMonth(),
        trialEnd.getDate()
      )
      const currentDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      )

      const remaining = Math.floor(
        (trialEndDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
      )

      setTrialDays(remaining > 0 ? remaining : 0)
    } else {
      setTrialDays(0)
    }
  }, [org])

  // Real-time validation for Linq Blue vs Linq One quantities
  useEffect(() => {
    if (subscriptionItems.length === 0) {
      setLinqQuantityError(false)
    }
  }, [subscriptionItems])

  const handleBackToOrganization = () => {
    history.push(`/organizations/${orgId}`)
  }

  const handleSubmit = async () => {
    if (!org) return

    const itemsArray = Array.isArray(subscriptionItems)
      ? subscriptionItems
      : (subscriptionItems as any)?.items || []

    if (itemsArray.length === 0) {
      toast.error('Please add at least one subscription item')
      return
    }

    const invalidQuantityItem = itemsArray.find(
      (item: any) => !item.quantity || item.quantity <= 0
    )
    if (invalidQuantityItem) {
      toast.error(
        `Quantity must be greater than 0 for ${
          invalidQuantityItem.productName || 'subscription item'
        }`
      )
      return
    }

    const linqBlueItem = itemsArray.find(
      (item: any) =>
        LINQ_BLUE_PRODUCT_IDS.includes(item.product_id) ||
        LINQ_BLUE_INFRASTRUCTURE_PRODUCT_ID.includes(item.product_id)
    )

    // If we have a Linq Blue item, we MUST have a Linq ONE item
    if (!linqBlueItem) {
      toast.error('Subscription must include Linq Blue')
      return
    }

    if (itemsArray.length > 1) {
      const billingIntervals = itemsArray
        .map((item: any) => {
          if (item.priceDetails?.recurring?.interval) {
            return item.priceDetails.recurring.interval
          }

          if (item.price?.recurring?.interval) {
            return item.price.recurring.interval
          }
          return null
        })
        .filter(Boolean)

      const uniqueIntervals = [...new Set(billingIntervals)]
      if (uniqueIntervals.length > 1) {
        toast.error(
          'Please check the line items - all subscription items must have the same billing interval'
        )
        return
      }
    }

    if (!selectedPaymentMethodId) {
      toast.error('Please select a payment method')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch(
        `${BASE_API_URL}/admin/api/v2/organizations/${org.id}/update_subscription_with_billing`,
        {
          method: 'POST',
          headers: apiHeaders(store.getState()),
          body: JSON.stringify({
            subscription_items: itemsArray,
            reset_billing_interval: resetBillingInterval,
            proration_enabled: prorationSettings.enabled,
            proration_behavior: prorationSettings.behavior,
            trial_days: trialDays,
            // Pass applied coupons as Stripe discounts payload
            discounts: (coupons || [])
              .map((c: any) => c?.couponDetails?.id || c?.id)
              .filter(Boolean)
              .map((id: string) => ({ coupon: id })),
            metadata: metadata.reduce((acc, item) => {
              if (item.key && item.value) {
                acc[item.key] = item.value
              }
              return acc
            }, {} as Record<string, string>),
            payment_method_id: selectedPaymentMethodId,
            // Only include payment_due_days if email invoice is selected (no payment method)
            ...(selectedPaymentMethodId === 'email_invoice_option' && {
              payment_due_days: paymentDueDays,
            }),
          }),
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      toast.success('Subscription updated successfully!')
      history.push(`/organizations/${orgId}`)
    } catch (error: any) {
      toast.error(error.message || 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isFetching) {
    return (
      <div style={{ backgroundColor: 'white', minHeight: '100vh' }}>
        <Container>
          <div className="m-2">Loading...</div>
        </Container>
      </div>
    )
  }

  if (!org) {
    return (
      <div style={{ backgroundColor: 'white', minHeight: '100vh' }}>
        <Container>
          <div>Organization not found</div>
        </Container>
      </div>
    )
  }

  return (
    <div
      style={{ backgroundColor: 'white', height: '100vh', overflow: 'hidden' }}
    >
      <Container
        fluid
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ flexShrink: 0 }}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href="/organizations">
              Organizations
            </Breadcrumb.Item>
            <Breadcrumb.Item href={`/organizations/${orgId}`}>
              {org.name}
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Stripe Update</Breadcrumb.Item>
          </Breadcrumb>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
              <Button
                variant="outline-secondary"
                onClick={handleBackToOrganization}
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: '#32325d',
                  padding: '20px 24px',
                  marginRight: '24px',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginTop: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = STRIPE_PRIMARY
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#32325d'
                }}
              >
                ← Back
              </Button>
              <h1 style={{ margin: 0, marginTop: '8px' }}>
                Update a subscription
              </h1>
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 0,
          }}
        >
          <div style={{ display: 'flex', width: '1600px', maxWidth: '95%' }}>
            {/* Left Container - Subscription Configuration */}
            <div style={{ flex: 1, height: '100%' }}>
              <div
                style={{
                  padding: '15px',
                  borderRadius: '4px',
                  height: '100%',
                  overflow: 'auto',
                  width: '100%',
                  borderRight: '1px solid #c1c7cd',
                  border: '1px solid #c1c7cd',
                }}
              >
                {org && (
                  <SubscriptionConfiguration
                    org={org}
                    onSubscriptionItemsChange={setSubscriptionItems}
                    onOriginalSubscriptionItemsChange={
                      setOriginalSubscriptionItems
                    }
                    onResetBillingIntervalChange={setResetBillingInterval}
                    onProrationSettingsChange={setProrationSettings}
                    onTrialDaysChange={setTrialDays}
                    onMetadataChange={setMetadata}
                    onPaymentMethodChange={setSelectedPaymentMethodId}
                    onPaymentDueDaysChange={setPaymentDueDays}
                    onCouponsChange={setCoupons}
                    invoicePreview={invoicePreviewData}
                  />
                )}
              </div>
            </div>

            {/* Right Container - Invoice Preview */}
            <div style={{ flex: 1, height: '100%' }}>
              <div
                style={{
                  padding: '15px',
                  borderRadius: '2px',
                  height: '100%',
                  overflow: 'auto',
                  width: '100%',
                  borderLeft: '1px solid #c1c7cd',
                }}
              >
                {org && (
                  <InvoicePreview
                    org={org}
                    updatedSubscriptionItems={subscriptionItems}
                    originalSubscriptionItems={originalSubscriptionItems}
                    resetBillingInterval={resetBillingInterval}
                    prorationSettings={prorationSettings}
                    trialDays={trialDays}
                    metadata={metadata}
                    selectedPaymentMethodId={selectedPaymentMethodId}
                    paymentDueDays={paymentDueDays}
                    coupons={coupons}
                    onErrorChange={setPreviewError}
                    onPreviewDataChange={setInvoicePreviewData}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '15px',
              borderTop: '1px solid #e6ebf1',
              backgroundColor: '#f8f9fa',
            }}
          >
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !org ||
                org.organization_subscription?.subscription_status ===
                  'canceled' ||
                subscriptionItems.length === 0 ||
                previewError ||
                linqQuantityError
              }
              style={{
                backgroundColor:
                  !org ||
                  org.organization_subscription?.subscription_status ===
                    'canceled' ||
                  subscriptionItems.length === 0 ||
                  previewError ||
                  linqQuantityError
                    ? '#6b7c93'
                    : STRIPE_PRIMARY,
                borderColor:
                  !org ||
                  org.organization_subscription?.subscription_status ===
                    'canceled' ||
                  subscriptionItems.length === 0 ||
                  previewError ||
                  linqQuantityError
                    ? '#6b7c93'
                    : STRIPE_PRIMARY,
                color: 'white',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: '600',
              }}
              onMouseEnter={(e) => {
                if (
                  org &&
                  org.organization_subscription?.subscription_status !==
                    'canceled' &&
                  subscriptionItems.length > 0 &&
                  !previewError &&
                  !linqQuantityError
                ) {
                  e.currentTarget.style.backgroundColor = STRIPE_PRIMARY_HOVER
                  e.currentTarget.style.borderColor = STRIPE_PRIMARY_HOVER
                }
              }}
              onMouseLeave={(e) => {
                if (
                  org &&
                  org.organization_subscription?.subscription_status !==
                    'canceled' &&
                  subscriptionItems.length > 0 &&
                  !previewError &&
                  !linqQuantityError
                ) {
                  e.currentTarget.style.backgroundColor = STRIPE_PRIMARY
                  e.currentTarget.style.borderColor = STRIPE_PRIMARY
                }
              }}
            >
              {isSubmitting
                ? 'Updating Subscription...'
                : !org
                ? 'Loading...'
                : org.organization_subscription?.subscription_status ===
                  'canceled'
                ? 'Subscription Canceled'
                : subscriptionItems.length === 0
                ? 'No Subscription Items'
                : previewError
                ? 'Cannot Update'
                : linqQuantityError
                ? 'Invalid Quantities'
                : 'Update Subscription'}
            </Button>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default StripeUpdatePage
